/**
 * What this client knows about its room. Server events are applied here;
 * the renderer only reads it. All times are on the local clock (server times
 * are shifted by the offset measured at welcome).
 */
import type { Look } from '../../../shared/cosmetics.ts'
import { endTile, planWalk, posAt, type Path } from '../../../shared/path.ts'
import { EMOTE_MS, type Act, type ChatLine, type Emote, type PlayerView, type Pose, type ServerMsg } from '../../../shared/protocol.ts'
import { cellAt, grid, ROOMS, SEAT, type RoomDef, type RoomId } from '../../../shared/world.ts'

export interface Player {
  id: string
  name: string
  look: Look
  path: Path
  pose: Pose
  face: 1 | -1
  emote?: { e: Emote; until: number }
  act?: { a: Act; at: number }
  bubble?: { text: string; at: number; until: number }
  /** Random per blob, so a crowd does not breathe in unison. */
  phase: number
}

export type BodyState = 'walk' | 'stand' | 'sit' | 'nap'

export interface Derived { x: number; y: number; state: BodyState; face: 1 | -1; seat: boolean }

const ACT_MS = 800

export class World {
  room: RoomDef = ROOMS.town
  shard = 1
  me = ''
  players = new Map<string, Player>()
  chat: ChatLine[] = []
  /** server clock − local clock */
  offset = 0
  /** Locally hidden players (mute/report). */
  hidden = new Set<string>()

  get ready(): boolean { return !!this.me && this.players.has(this.me) }
  get self(): Player | undefined { return this.players.get(this.me) }

  private local(path: Path): Path { return { t0: path.t0 - this.offset, pts: path.pts } }

  private add(v: PlayerView, now: number): Player {
    const p: Player = { id: v.id, name: v.name, look: v.look, path: this.local(v.path), pose: v.pose, face: v.face, phase: Math.random() * 10 }
    if (v.emote) {
      const until = v.emote.at - this.offset + EMOTE_MS
      if (until > now) p.emote = { e: v.emote.e, until }
    }
    this.players.set(p.id, p)
    return p
  }

  /** Apply a server event. Returns a chat line when one should be logged. */
  apply(m: ServerMsg, now: number): ChatLine | undefined {
    switch (m.t) {
      case 'welcome':
        this.offset = m.now - now
        this.room = ROOMS[m.room]
        this.shard = m.shard
        this.me = m.you
        this.players.clear()
        for (const v of m.players) this.add(v, now)
        this.chat = m.chat.slice()
        return
      case 'join':
        this.add(m.p, now)
        return
      case 'leave':
        this.players.delete(m.id)
        return
      case 'path': {
        const p = this.players.get(m.id)
        if (!p) return
        const path = this.local(m.path)
        if (m.id === this.me) {
          // We predicted our own walk; only take the server's when it disagrees on where we end up.
          const [ax, ay] = endTile(p.path), [bx, by] = endTile(path)
          if (ax === bx && ay === by) return
        }
        p.path = path
        p.pose = 'stand'
        return
      }
      case 'say': {
        const p = this.players.get(m.id)
        const line = { id: m.id, name: m.name, text: m.text, at: m.at - this.offset }
        if (this.hidden.has(m.id)) return
        if (p) p.bubble = { text: m.text, at: now, until: now + 4500 + Math.min(6000, m.text.length * 60) }
        this.chat.push(line)
        if (this.chat.length > 120) this.chat.splice(0, this.chat.length - 120)
        return line
      }
      case 'emote': {
        const p = this.players.get(m.id)
        if (p) p.emote = { e: m.e, until: now + EMOTE_MS }
        return
      }
      case 'act': {
        const p = this.players.get(m.id)
        if (p && m.id !== this.me) p.act = { a: m.a, at: now }
        return
      }
      case 'pose': {
        const p = this.players.get(m.id)
        if (p) { p.pose = m.p; p.face = m.face }
        return
      }
      case 'look': {
        const p = this.players.get(m.id)
        if (p) p.look = m.look
        return
      }
      case 'notice':
        return
    }
  }

  /** Predict our own walk; returns false when there is no way there. */
  walkTo(x: number, y: number, now: number): boolean {
    const me = this.self
    if (!me) return false
    const path = planWalk(grid(this.room.id), me.path, now, x, y)
    if (!path) return false
    me.path = path
    me.pose = 'stand'
    return true
  }

  /** Where a player is and what their body is doing at `now`. */
  derive(p: Player, now: number): Derived {
    const at = posAt(p.path, now)
    let face = p.face
    if (at.moving) {
      const sx = at.dx - at.dy
      if (Math.abs(sx) > 0.01) face = sx > 0 ? 1 : -1
      p.face = face
      return { x: at.x, y: at.y, state: 'walk', face, seat: false }
    }
    const g = grid(this.room.id)
    const tx = Math.floor(at.x), ty = Math.floor(at.y)
    if (cellAt(g, tx, ty) === SEAT) return { x: at.x, y: at.y, state: 'sit', face: g.facing.get(ty * g.w + tx) ?? face, seat: true }
    return { x: at.x, y: at.y, state: p.pose === 'nap' ? 'nap' : p.pose === 'sit' ? 'sit' : 'stand', face, seat: false }
  }

  emoteOf(p: Player, now: number): Emote | 'idle' {
    if (p.emote && p.emote.until > now) return p.emote.e
    return p.pose === 'nap' ? 'sleepy' : 'idle'
  }

  actOf(p: Player, now: number): { a: Act; k: number } | undefined {
    if (!p.act) return undefined
    const k = (now - p.act.at) / ACT_MS
    if (k >= 1) { p.act = undefined; return undefined }
    return { a: p.act.a, k }
  }
}

export function roomOf(id: RoomId): RoomDef { return ROOMS[id] }
